import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { SearchDocumentsEntity, SearchDocumentsFilterEntity } from './search.models';
import { Action, createReducer, on } from '@ngrx/store';
import * as SearchActions from './search.actions';

export const SEARCH_DOCUMENS_FEATURE_KEY = 'searchDocuments';

export interface SearchDocumentsState extends EntityState<SearchDocumentsEntity> {
  query: string;
  results: SearchDocumentsEntity[];
  filters: SearchDocumentsFilterEntity[];
  total_items: number;
  current_page: number;
  loaded: boolean;
  error?: string | null;
  lastRequest: SearchActions.SearchDocumentsRequest | null;
}

export const searchDocumentsAdapter: EntityAdapter<SearchDocumentsEntity> =
  createEntityAdapter<SearchDocumentsEntity>();

export const initialSearchDocumentsState: SearchDocumentsState = searchDocumentsAdapter.getInitialState({
  query: '',
  results: [],
  filters: [],
  total_items: 0,
  current_page: 1,
  loaded: false,
  lastRequest: null,
});

const reducer = createReducer(
  initialSearchDocumentsState,
  on(SearchActions.searchDocuments, (state, action) => ({
    ...state,
    loaded: false,
    error: null,
    lastRequest: {
      categoryId: action.categoryId,
      language: action.language,
      filterIds: action.filterIds,
      searchTerm: action.searchTerm,
      limit: action.limit,
      page: action.page,
      searchType: action.searchType,
      sort: action.sort,
      collectionId: action.collectionId,
    },
  })),
  on(SearchActions.loadDocumentsSuccess, (state, { data }) => ({
    ...state,
    ...data,
    loaded: true,
    error: null,
  })),
  on(SearchActions.loadDocumentsFailure, (state, { error }) => ({
    ...state,
    loaded: true,
    error,
  })),
  on(SearchActions.resetSearchDocuments, () => ({
    ...initialSearchDocumentsState,
  })),
  on(SearchActions.updateSearchDocumentBookmark, (state, { documentId, isBookmarked }) => ({
    ...state,
    // A document that is no longer bookmarked is not part of any collection either
    results: state.results.map((doc) =>
      doc.id === documentId
        ? { ...doc, is_bookmarked: isBookmarked, ...(isBookmarked ? {} : { collections: [] }) }
        : doc,
    ),
  })),
  on(SearchActions.addCollectionToSearchDocuments, (state, { documentIds, collection }) => ({
    ...state,
    results: state.results.map((doc) =>
      documentIds.includes(doc.id) && !doc.collections?.some((existing) => existing.id === collection.id)
        ? { ...doc, collections: [...(doc.collections ?? []), collection] }
        : doc,
    ),
  })),
  on(SearchActions.renameCollectionInSearchDocuments, (state, { collectionId, name }) => ({
    ...state,
    results: state.results.map((doc) =>
      doc.collections?.some((collection) => collection.id === collectionId)
        ? {
            ...doc,
            collections: doc.collections.map((collection) =>
              collection.id === collectionId ? { ...collection, name } : collection,
            ),
          }
        : doc,
    ),
  })),
  on(SearchActions.removeCollectionFromSearchDocuments, (state, { collectionId }) => ({
    ...state,
    results: state.results.map((doc) =>
      doc.collections?.some((collection) => collection.id === collectionId)
        ? { ...doc, collections: doc.collections.filter((collection) => collection.id !== collectionId) }
        : doc,
    ),
  })),
);

export function searchDocumentsReducer(state: SearchDocumentsState | undefined, action: Action) {
  return reducer(state, action);
}
